import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Mail, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Card, Empty, NativeSelect, PageHeader, Spinner } from "@/components/Common";
import { api, asList, enc, errMsg, tzOffset } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { defaultSearch, withLocation } from "@/lib/profileSearch";
import { MorningEmailCard } from "@/components/plan/PlanParts";
import { usePaidPlan } from "@/lib/weekPlan";

const BLANK = { query: "", remote: false, employment_type: "", date_posted: "all", source: "boards", board: "all" };

const Digest = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/alerts/digest").then((r) => setD(r.data)).catch(() => {}); }, []);
  const toggle = async (enabled) => {
    try { await api.put("/alerts/digest", { enabled, tz_offset_minutes: tzOffset() }); setD({ ...d, enabled }); toast.success(enabled ? "Daily digest on" : "Daily digest off"); } catch (e) { toast.error(errMsg(e)); }
  };
  const send = async () => { try { await api.post("/alerts/digest/send-now"); toast.success("Digest sent to your inbox"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Card data-testid="digest-card">
      <div className="flex items-center justify-between"><div className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#D4AF37]" /><h3 className="font-display text-lg text-zinc-100">Daily digest</h3></div>
        <Switch checked={!!d?.enabled} onCheckedChange={toggle} data-testid="digest-switch" /></div>
      <p className="mt-2 text-sm text-zinc-500">A morning email with new matches from all your alerts.</p>
      <Button variant="outline" onClick={send} className="mt-4 w-full" data-testid="digest-send-now-button">Send me one now</Button>
    </Card>
  );
};

export default function Alerts() {
  const { profile } = useAuth();
  const paidPlan = usePaidPlan();
  const blank = () => { const d = defaultSearch(profile); return { ...BLANK, query: withLocation(d.query, d.location), remote: d.remote }; };
  const [alerts, setAlerts] = useState(null);
  const [form, setForm] = useState(blank);
  const [checking, setChecking] = useState(false);
  const navigate = useNavigate();
  const load = () => api.get("/alerts").then((r) => setAlerts(asList(r.data, "alerts"))).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const suggestions = (() => {
    const d = defaultSearch(profile);
    const have = new Set((alerts || []).map((a) => a.query.toLowerCase()));
    return (profile?.target_roles || []).slice(0, 3).map((r) => withLocation(r, d.location)).filter((q) => !have.has(q.toLowerCase()));
  })();
  const [creating, setCreating] = useState(false);
  const createFromTargets = async () => {
    setCreating(true);
    try {
      for (const q of suggestions) await api.post("/alerts", { ...BLANK, query: q, remote: defaultSearch(profile).remote, employment_type: null });
      toast.success(`${suggestions.length} alert${suggestions.length > 1 ? "s" : ""} created from your targets`);
    } catch (e) { toast.error(errMsg(e)); } finally { await load(); setCreating(false); }
  };

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/alerts", { ...form, employment_type: form.employment_type || null }); setForm({ ...BLANK, remote: form.remote }); toast.success("Alert created"); load(); } catch (e2) { toast.error(errMsg(e2)); }
  };
  const del = async (id) => { try { await api.delete(`/alerts/${enc(id)}`); setAlerts((a) => a.filter((x) => x.alert_id !== id)); } catch (e) { toast.error(errMsg(e)); } };
  const check = async () => {
    setChecking(true);
    try { const { data } = await api.post("/alerts/check", null, { params: { force: true } }); if (Array.isArray(data?.alerts)) setAlerts(data.alerts); else await load(); toast.success("Alerts refreshed"); } catch (e) { toast.error(errMsg(e)); } finally { setChecking(false); }
  };
  const open = (a) => {
    api.post(`/alerts/${enc(a.alert_id)}/seen`).catch(() => {});
    navigate("/search", { state: { alert: a } });
  };

  return (
    <div>
      <PageHeader title="Job Alerts" subtitle="Saved searches that watch the market for you.">
        <Button variant="outline" onClick={check} disabled={checking} data-testid="check-alerts-button">{checking ? <Spinner /> : <RefreshCw className="h-4 w-4" />}Check now</Button>
      </PageHeader>
      <div className="grid gap-6 xl:grid-cols-[1fr_340px]">
        <div className="space-y-6">
          {suggestions.length > 0 && (
            <div className="flex flex-col gap-3 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="alert-suggestions">
              <div className="text-sm text-zinc-300">Watch your target roles: <span className="text-[#F3C649]">{suggestions.join(" · ")}</span></div>
              <Button size="sm" onClick={createFromTargets} disabled={creating} data-testid="create-target-alerts-button">{creating ? <Spinner /> : `Create ${suggestions.length} alert${suggestions.length > 1 ? "s" : ""}`}</Button>
            </div>
          )}
          <Card>
            <form onSubmit={create} className="flex flex-col gap-3 lg:flex-row lg:items-center">
              <Input value={form.query} onChange={(e) => setForm({ ...form, query: e.target.value })} placeholder="e.g. Staff product designer" className="bg-[#09090B] lg:flex-1" required data-testid="alert-query-input" />
              <NativeSelect value={form.source} onChange={(v) => setForm({ ...form, source: v })} options={[{ value: "boards", label: "Job boards" }, { value: "ats", label: "ATS direct (Elite)" }]} data-testid="alert-source-select" />
              <NativeSelect value={form.date_posted} onChange={(v) => setForm({ ...form, date_posted: v })} options={["all", "week", "month"].map((d) => ({ value: d, label: d === "all" ? "Any time" : `Past ${d}` }))} data-testid="alert-date-select" />
              <label className="flex items-center gap-2 text-sm text-zinc-300"><Switch checked={form.remote} onCheckedChange={(v) => setForm({ ...form, remote: v })} data-testid="alert-remote-switch" />Remote</label>
              <Button type="submit" data-testid="create-alert-button">Create alert</Button>
            </form>
          </Card>
          {!alerts ? <Spinner className="h-6 w-6 text-[#D4AF37]" /> : alerts.length === 0 ? <Empty icon={Bell} title="No alerts yet" text="Create one above and we'll flag new roles as they appear." /> : (
            <div className="space-y-3" data-testid="alerts-list">
              {alerts.map((a) => (
                <div key={a.alert_id} onClick={() => open(a)} data-testid={`alert-${a.alert_id}`}
                  className="fade-up flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-[#27272A] bg-[#18181B] p-4 transition-colors hover:border-[#D4AF37]/40">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><span className="truncate font-display text-base text-zinc-100">{a.query}</span>
                      {a.new_count > 0 && <span className="rounded-full bg-[#D4AF37] px-2 py-0.5 text-[10px] font-bold text-black" data-testid={`alert-new-${a.alert_id}`}>{a.new_count} NEW</span>}</div>
                    <div className="mt-1 font-mono text-[11px] uppercase tracking-wider text-zinc-500">{a.source === "ats" ? `ATS · ${a.board}` : "Job boards"} · {a.date_posted}{a.remote && " · remote"}</div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); del(a.alert_id); }} className="text-zinc-500 hover:text-red-400" data-testid={`delete-alert-${a.alert_id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="space-y-6">
          <Digest />
          {paidPlan && <MorningEmailCard />}
        </div>
      </div>
    </div>
  );
}
